import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Space,
  Statistic,
  Button,
  Spin,
  message,
  Tag,
  Tooltip,
} from 'antd';
import {
  TrophyOutlined,
  RiseOutlined,
  GiftOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { gamificationApi } from '../../api';
import { RankIcon, ExpProgress, getRankByExp } from './RankComponents';

/**
 * ゲーミフィケーションサマリーカード
 * ダッシュボードやヘッダーに表示される概要
 */
const GamificationSummary = ({ compact = false, onRankUp }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [luckyBonus, setLuckyBonus] = useState(null);
  const checkRankUpRef = useRef(false); // ランクアップチェック済みフラグ
  const initialLoadDone = useRef(false); // 初回読み込み完了フラグ

  useEffect(() => {
    // ランクアップモーダルの表示を遅延させる
    // ダッシュボードツアーが先に開始されるように、1秒待ってから実行
    const timer = setTimeout(() => {
      loadStatus();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const loadStatus = async () => {
    try {
      const data = await gamificationApi.getStatus();

      setStatus(data);

      // ランクアップモーダル表示の前に、アンケートとダッシュボードツアーの完了を確認
      // 順序: ①アンケート → ②ダッシュボードツアー → ③ランクアップモーダル
      let canShowRankUp = true;

      // アンケートが未完了の場合はランクアップを表示しない
      if (!data.survey_completed) {
        canShowRankUp = false;
      }

      // ダッシュボードツアーの完了状態を確認
      if (canShowRankUp) {
        try {
          const tourResponse = await gamificationApi.getTutorialTours();
          const dashboardTourCompleted = tourResponse.tours?.dashboard || false;
          if (!dashboardTourCompleted) {
            canShowRankUp = false;
          }
        } catch (tourError) {
          console.error('Failed to check tour status:', tourError);
        }
      }

      // APIレスポンスにrank_updateが含まれている場合、ランクアップモーダルを表示
      // バックエンドでlast_notified_rankとcurrent_rankを比較して判定
      if (
        data.rank_update &&
        onRankUp &&
        !checkRankUpRef.current &&
        canShowRankUp
      ) {
        onRankUp(data.rank_update);
        checkRankUpRef.current = true;

        // モーダル表示後、バックエンドに通知済みをマーク
        try {
          await gamificationApi.markRankNotified();
        } catch (error) {
          console.error('Failed to mark rank as notified:', error);
        }
      }

      // ラッキーボーナスチェック
      if (data.lucky_bonus) {
        setLuckyBonus(data.lucky_bonus);
        message.success({
          content: data.lucky_bonus.message,
          duration: 5,
          icon: <GiftOutlined style={{ color: '#faad14' }} />,
        });
      }
    } catch (error) {
      console.error('Failed to load gamification status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (!status) {
    return null;
  }

  const { currentRank, nextRank } = getRankByExp(status.total_exp);

  // コンパクト表示（ヘッダー用）
  if (compact) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 16px',
          background: '#fafafa',
          borderRadius: 8,
          cursor: 'pointer',
        }}
        onClick={() => navigate('/rank-system')}
      >
        <RankIcon rank={currentRank.rank} size={24} />
        <div style={{ minWidth: 100 }}>
          <ExpProgress
            totalExp={status.total_exp}
            showNumbers={false}
            size="small"
          />
        </div>
        <Tooltip title="ランクシステムを見る">
          <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
        </Tooltip>
      </div>
    );
  }

  // フル表示（ダッシュボード用）
  return (
    <Card
      title={
        <Space>
          <TrophyOutlined style={{ color: currentRank.color }} />
          <span>ランク進捗</span>
        </Space>
      }
      extra={
        <Button
          type="link"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate('/rank-system')}
        >
          詳細を見る
        </Button>
      }
      style={{ height: '100%' }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 現在のランクとEXP */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <RankIcon rank={currentRank.rank} size={36} />
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color:
                      currentRank.rank === 'white_1'
                        ? '#595959'
                        : currentRank.color,
                    textShadow:
                      currentRank.rank === 'white_1'
                        ? '0 0 1px rgba(0,0,0,0.3)'
                        : 'none',
                  }}
                >
                  {currentRank.display_name}
                </div>
                <div style={{ fontSize: 14, color: '#8c8c8c', marginTop: 4 }}>
                  総EXP:{' '}
                  <span style={{ fontWeight: 600 }}>{status.total_exp}</span>
                </div>
              </div>
            </div>

            {luckyBonus && (
              <Tag color="gold" icon={<GiftOutlined />}>
                ラッキー +{luckyBonus.amount} EXP
              </Tag>
            )}
          </div>

          {/* EXP進捗バーと次の特典（吹き出しデザイン） */}
          <div style={{ position: 'relative' }}>
            {nextRank && nextRank.rewards && nextRank.rewards.length > 0 && (
              <div
                className="next-benefit-tooltip"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: -80,
                  maxWidth: '240px',
                  padding: '8px 12px',
                  background: '#fff7e6',
                  borderRadius: 8,
                  border: '1px solid #ffd591',
                  fontSize: 11,
                  lineHeight: 1.4,
                  color: '#d46b08',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  zIndex: 10,
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#d46b08',
                    marginBottom: 4,
                  }}
                >
                  次の特典
                </div>
                {nextRank.rewards
                  .map((reward, idx) => {
                    if (reward.type === 'bonus_credit') {
                      return (
                        <div key={idx}>ボーナスクレジット +{reward.amount}</div>
                      );
                    } else if (reward.type === 'feature_unlock') {
                      const featureNames = {
                        'referral_system': '紹介機能',
                        'fact_check_mode': '???',
                      };
                      return (
                        <div key={idx}>
                          {featureNames[reward.feature] || reward.feature}
                        </div>
                      );
                    } else if (reward.type === 'permanent_discount' || reward.type === 'discount') {
                      return (
                        <div key={idx}>
                          {reward.description || `${Math.round((reward.rate || 0) * 100)}% 割引`}
                        </div>
                      );
                    }
                    return null;
                  })
                  .slice(0, 2)}
                {nextRank.rewards.length > 2 && (
                  <div>他{nextRank.rewards.length - 2}件</div>
                )}
                {/* 下向き三角形（吹き出しの尾） */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: -6,
                    right: 20,
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #ffd591',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: -5,
                    right: 21,
                    width: 0,
                    height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: '5px solid #fff7e6',
                  }}
                />
              </div>
            )}

            <ExpProgress totalExp={status.total_exp} size="default" />
          </div>
        </div>

        {/* 連続ログインボーナス進捗 */}
        {status.consecutive_login_days > 0 && (
          <div
            style={{
              padding: 16,
              background: '#f0f9ff',
              borderRadius: 8,
              border: '1px solid #91d5ff',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 600 }}>連続ログイン</span>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#1890ff' }}>
                {status.consecutive_login_days} 日
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#595959' }}>
              {status.consecutive_login_days >= 7
                ? '7日連続達成！次は30日連続を目指しましょう'
                : `7日連続で +10 クレジット (残り ${7 - status.consecutive_login_days}日)`}
            </div>
          </div>
        )}

        {/* 紹介機能アンロック状態 */}
        {status.referral_unlocked && (
          <Button
            type="dashed"
            block
            icon={<GiftOutlined />}
            onClick={() => navigate('/aio/referral')}
          >
            友達を紹介して {status.referral_reward || 50} クレジット獲得
          </Button>
        )}

        {!status.referral_unlocked && nextRank && (
          <div style={{ fontSize: 13, color: '#8c8c8c', textAlign: 'center' }}>
            <RiseOutlined /> ブロンズ III で紹介機能がアンロックされます
          </div>
        )}
      </Space>
    </Card>
  );
};

export default GamificationSummary;
